import Header from "../components/header";
import DefaultFooter from "../components/default-footer";
import WhyChoose from "./../components/BlockGuide";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSeo } from "../components/useSeo";
import { breadcrumbJsonLd } from "../components/seoData";

// Placeholder photo set for the About-section carousel. Same caveat as
// before: these are labeled color-block placeholders, not real photos —
// swap the `img` values for your own team/office/Oman photography.
const ABOUT_SLIDES = [
  { img: `${process.env.PUBLIC_URL}/photo1.jpg`, caption: "Our Team" },
  { img: `${process.env.PUBLIC_URL}/photo2.webp`, caption: "Our Office" },
  { img: `${process.env.PUBLIC_URL}/photo3.webp`, caption: "Happy Clients" },
  { img: `${process.env.PUBLIC_URL}/photo4.webp`, caption: "Oman" },
];
const ABOUT_SLIDE_INTERVAL = 4000;

const About = () => {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "About Us | SilverJarz Travel & Tourism",
    description: "SilverJarz is a travel and tourism company based in Oman, helping travelers with tourist visas, flight tickets, and curated tour packages.",
    canonical: `${origin}/about`,
    jsonLd: breadcrumbJsonLd([
      { name: "Home", url: `${origin}/` },
      { name: "About", url: `${origin}/about` },
    ]),
  });


  const goToSlide = useCallback((idx: number) => {
    setActiveSlide(idx);
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % ABOUT_SLIDES.length);
    }, ABOUT_SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const values = [
    {
      icon: "✦",
      titleKey: "valueTitle1",
      descKey: "valueDesc1",
    },
    {
      icon: "⚡",
      titleKey: "valueTitle2",
      descKey: "valueDesc2",
    },
    {
      icon: "🛡",
      titleKey: "valueTitle3",
      descKey: "valueDesc3",
    },
    {
      icon: "✶",
      titleKey: "valueTitle4",
      descKey: "valueDesc4",
    },
  ];

  const tags = ["visas", "flightTickets", "insurance", "holidayPackages"];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        /* Tokens unified with the rest of the site (home, FAQ) — this
           page previously defined its own --teal-dark / --gold-light /
           Cormorant Garamond, which is why it read as a slightly
           different product. Same variable names as everywhere else now. */
        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
        }

        .about-page {
          font-family: 'DM Sans', sans-serif;
          background: #fff;
        }

        /* ── HERO ── */
        .hero-about {
          position: relative;
          height: 450px;
          overflow: hidden;
          display: flex;
          align-items: center;
          border-radius: 0 0 20px 20px;
        }
        .hero-about__bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center 40%;
        }
        .hero-about__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(41,75,90,0.75) 0%, rgba(26,49,64,0.55) 100%);
        }
        .hero-about__content {
          position: relative;
          z-index: 2;
          padding: 0 48px;
          max-width: 700px;
        }
        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 20px;
        }
        .hero-eyebrow::before {
          content: '';
          width: 20px;
          height: 2px;
          background: var(--gold);
        }
        .hero-about h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(38px, 4.6vw, 60px);
          font-weight: 600;
          color: #fff;
          line-height: 1.14;
          margin-bottom: 20px;
        }
        .hero-about h1 em {
          font-style: italic;
          color: var(--gold);
          font-weight: 400;
        }
        .hero-about p {
          color: rgba(255,255,255,0.75);
          font-size: 15px;
          line-height: 1.75;
          max-width: 460px;
          font-weight: 300;
        }

        /* ── STATS BAR ── */
        .stats-bar {
          background: var(--teal-d);
          padding: 0;
        }
        .stats-bar__inner {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        .stat-item {
          padding: 36px 20px;
          text-align: center;
          border-right: 1px solid rgba(255,255,255,0.08);
          transition: background 0.3s;
        }
        .stat-item:last-child { border-right: none; }
        .stat-item:hover { background: rgba(211,190,118,0.08); }
        .stat-item__value {
          font-family: 'Playfair Display', serif;
          font-size: 2.4rem;
          font-weight: 600;
          color: var(--gold);
          line-height: 1;
          margin-bottom: 8px;
        }
        .stat-item__label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
        }

        /* ── ABOUT SECTION ── */
        .about-content-section {
          padding: 84px 0;
          background: #fff;
        }
        @media (max-width: 991px) { .about-content-section { padding: 60px 0; } }
        .section-tag {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--gold-d);
          margin-bottom: 16px;
        }
        .section-tag::before {
          content: '';
          width: 22px;
          height: 2px;
          background: var(--gold);
        }
        .section-headline {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.9rem, 3vw, 2.7rem);
          font-weight: 600;
          color: var(--ink);
          line-height: 1.2;
          letter-spacing: -.01em;
          margin-bottom: 22px;
        }
        .about-text {
          color: var(--silver);
          font-size: 15px;
          line-height: 1.85;
          font-weight: 300;
        }
        .about-image-wrap {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 30px 80px rgba(26,49,64,0.18);
          height: clamp(380px, 38vw, 520px);
        }
        .about-image-slide {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity .9s ease-in-out;
        }
        .about-image-slide.is-active { opacity: 1; z-index: 1; }
        .about-image-slide.is-hidden { opacity: 0; z-index: 0; }
        .about-image-veil {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(26,49,64,0) 60%, rgba(26,49,64,.45) 100%);
          pointer-events: none;
          z-index: 2;
        }
        .about-image-dots {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 3;
          display: flex;
          gap: 8px;
        }
        .about-image-dot {
          height: 4px;
          border-radius: 2px;
          border: none;
          cursor: pointer;
          background: rgba(255,255,255,.35);
          transition: background .3s, width .3s;
          width: 16px;
        }
        .about-image-dot.on { background: var(--gold); width: 30px; }

        /* ── MISSION / VISION ── */
        .mv-section {
          background: var(--cream);
          padding: 84px 0;
        }
        @media (max-width: 991px) { .mv-section { padding: 60px 0; } }
        .mv-card {
          background: #fff;
          border-radius: 16px;
          padding: 44px 40px;
          height: 100%;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(41,75,90,.09);
          transition: transform 0.3s, box-shadow 0.3s, border-color .3s;
        }
        .mv-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 48px rgba(26,49,64,0.12);
          border-color: rgba(211,190,118,.35);
        }
        .mv-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--teal) 0%, var(--gold) 100%);
        }
        .mv-card__icon {
          width: 54px;
          height: 54px;
          border-radius: 14px;
          background: rgba(41,75,90,0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 22px;
        }
        .mv-card__label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--gold-d);
          margin-bottom: 12px;
        }
        .mv-card__title {
          font-family: 'Playfair Display', serif;
          font-size: 1.6rem;
          font-weight: 600;
          color: var(--ink);
          margin-bottom: 14px;
          line-height: 1.25;
        }
        .mv-card__text {
          color: var(--silver);
          font-size: 14.5px;
          line-height: 1.8;
          font-weight: 300;
        }

        .mv-icon-svg {
          width: 26px;
          height: 26px;
          stroke: var(--teal);
          stroke-width: 1.5;
          fill: none;
        }

        /* ── VALUES ── */
        .values-section {
          background: var(--teal-d);
          padding: 84px 0;
          position: relative;
          overflow: hidden;
        }
        @media (max-width: 991px) { .values-section { padding: 60px 0; } }
        .values-section::before {
          content: '';
          position: absolute;
          top: -80px; right: -80px;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: rgba(211,190,118,0.06);
          pointer-events: none;
        }
        .values-section::after {
          content: '';
          position: absolute;
          bottom: -100px; left: -50px;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: rgba(255,255,255,0.03);
          pointer-events: none;
        }
        .value-item {
          padding: 34px 30px;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          transition: background 0.3s, border-color 0.3s, transform 0.3s;
          cursor: default;
          height: 100%;
        }
        .value-item:hover {
          background: rgba(211,190,118,0.07);
          border-color: rgba(211,190,118,0.3);
          transform: translateY(-4px);
        }
        .value-item__icon {
          font-size: 20px;
          color: var(--gold);
          margin-bottom: 18px;
          display: block;
        }
        .value-item__title {
          font-family: 'Playfair Display', serif;
          font-size: 1.25rem;
          font-weight: 600;
          color: #fff;
          margin-bottom: 10px;
        }
        .value-item__desc {
          color: rgba(255,255,255,0.5);
          font-size: 13.5px;
          line-height: 1.75;
          font-weight: 300;
        }

        /* ── WHY CHOOSE ── */
        .why-section {
          padding: 84px 0;
          background: #fff;
        }
        @media (max-width: 991px) { .why-section { padding: 60px 0; } }

        @media (max-width: 768px) {
          .stats-bar__inner { grid-template-columns: repeat(2,1fr); }
          .hero-about__content { padding: 0 24px; }
        }
        @media (max-width: 480px) {
          .stat-item { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.08); }
        }
      `}</style>

      <div className="about-page">
        <Header />

        {/*
          NOTE ON IMAGES: both photos below are placeholders — solid
          on-brand color blocks, not real photography. I can't preview
          external image URLs from this environment, so rather than
          guess again and risk another mismatch, these render reliably
          and are obviously "not final" until you swap them for either
          (a) your own photos, or (b) an Unsplash URL you've picked and
          viewed yourself. Just replace the two `src` values below.
        */}

        {/* ── HERO ── */}
        <section className="hero-about">
          <img
            className="hero-about__bg"
            src="./../img/about_us_bg.png"
            alt="Placeholder — replace with a real Oman coastline or Muscat photo"
          />
          <div className="hero-about__overlay"></div>
          <div className="hero-about__content">
            <div className="hero-eyebrow">{t("silverJarzTravel")}</div>
            <h1>
              {t("heroTitle1")}<br /><em>{t("heroTitle2")}</em>
            </h1>
            <p>
              {t("heroDescription")}
            </p>
          </div>
        </section>

        {/* ── ABOUT US ── */}
        <section className="about-content-section">
          <div className="container">
            <div className="row y-gap-50 align-items-center justify-between">
              <div className="col-lg-5">
                <div className="section-tag">{t("aboutUs")}</div>
                <h2 className="section-headline">
                  {t("aboutHeadline1")}<br />{t("aboutHeadline2")}
                </h2>
                <p className="about-text" dangerouslySetInnerHTML={{ __html: t("aboutText1") }} />
                <p className="about-text mt-20">
                  {t("aboutText2")}
                </p>
                <div style={{ marginTop: "40px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  {tags.map((tagKey) => (
                    <span key={tagKey} style={{
                      padding: "8px 20px",
                      background: "rgba(41,75,90,0.07)",
                      color: "var(--teal)",
                      borderRadius: "30px",
                      fontSize: "13px",
                      fontWeight: 500,
                      border: "1px solid rgba(41,75,90,0.12)"
                    }}>{t(tagKey)}</span>
                  ))}
                </div>
              </div>
              <div className="col-lg-6">
                <div className="about-image-wrap">
                  {ABOUT_SLIDES.map((slide, i) => (
                    <div
                      key={i}
                      className={`about-image-slide ${i === activeSlide ? "is-active" : "is-hidden"}`}
                      style={{ backgroundImage: `url(${slide.img})` }}
                    />
                  ))}
                  <div className="about-image-veil" />
                  <div className="about-image-dots">
                    {ABOUT_SLIDES.map((_, i) => (
                      <button
                        key={i}
                        className={`about-image-dot ${i === activeSlide ? "on" : ""}`}
                        onClick={() => goToSlide(i)}
                        aria-label={`Show photo ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── MISSION & VISION ── */}
        <section className="mv-section">
          <div className="container">
            <div className="row justify-center text-center mb-60">
              <div className="col-lg-7">
                <div className="section-tag" style={{ justifyContent: "center" }}>{t("ourPurpose")}</div>
                <h2 className="section-headline">{t("purposeHeadline1")}<br />{t("purposeHeadline2")}</h2>
              </div>
            </div>
            <div className="row y-gap-30">
              <div className="col-lg-6">
                <div className="mv-card">
                  <div className="mv-card__icon">
                    <svg className="mv-icon-svg" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" fill="none">
                      <path d="M12 2L15 8.5L22 9.5L17 14L18.5 21L12 17.5L5.5 21L7 14L2 9.5L9 8.5L12 2Z" />
                    </svg>
                  </div>
                  <div className="mv-card__label">{t("ourMission")}</div>
                  <div className="mv-card__title">{t("missionTitle")}</div>
                  <p className="mv-card__text">
                    {t("missionText")}
                  </p>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mv-card">
                  <div className="mv-card__icon">
                    <svg className="mv-icon-svg" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" fill="none">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2C14.5 4.5 16 8 16 12C16 16 14.5 19.5 12 22" />
                      <path d="M12 2C9.5 4.5 8 8 8 12C8 16 9.5 19.5 12 22" />
                    </svg>
                  </div>
                  <div className="mv-card__label">{t("ourVision")}</div>
                  <div className="mv-card__title">{t("visionTitle")}</div>
                  <p className="mv-card__text">
                    {t("visionText")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── VALUES ── */}
        <section className="values-section">
          <div className="container">
            <div className="row justify-center text-center mb-60">
              <div className="col-lg-7">
                <div className="section-tag" style={{ justifyContent: "center", color: "var(--gold)" }}>
                  {t("ourValues")}
                </div>
                <h2 className="section-headline" style={{ color: "#fff" }}>
                  {t("valuesHeadline1")}<br />{t("valuesHeadline2")}
                </h2>
              </div>
            </div>
            <div className="row y-gap-24">
              {values.map((v, i) => (
                <div className="col-lg-3 col-md-6" key={i}>
                  <div className="value-item">
                    <span className="value-item__icon">{v.icon}</span>
                    <div className="value-item__title">{t(v.titleKey)}</div>
                    <p className="value-item__desc">{t(v.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY CHOOSE US ── */}
        <section className="why-section">
          <div className="container">
            <div className="row justify-center text-center mb-50">
              <div className="col-lg-7">
                <div className="section-tag" style={{ justifyContent: "center" }}>{t("whyUs")}</div>
                <h2 className="section-headline">{t("whyHeadline1")}<br />{t("whyHeadline2")}</h2>
              </div>
            </div>
            <div className="row y-gap-40 justify-between">
              <WhyChoose />
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
};

export default About;